import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import connectorService from '../../services/connector.service';

interface NATTabProps {
  connectorId: string;
}

const NATTab = ({ connectorId }: NATTabProps) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [masqueradeInterface, setMasqueradeInterface] = useState('eth0');
  const [sourceRangesText, setSourceRangesText] = useState('');

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await connectorService.getNat(connectorId);
      setEnabled(data.enabled === 1);
      setMasqueradeInterface(data.masquerade_interface || 'eth0');

      let ranges: string[] = [];
      if (data.source_ranges) {
        try {
          ranges = typeof data.source_ranges === 'string'
            ? JSON.parse(data.source_ranges)
            : data.source_ranges as string[];
        } catch {
          ranges = [];
        }
      }
      setSourceRangesText(ranges.join('\n'));
    } catch {
      toast.error('Failed to load NAT config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfig(); }, [connectorId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const sourceRanges = sourceRangesText.split('\n').map(s => s.trim()).filter(s => s);
      await connectorService.updateNat(connectorId, {
        enabled,
        masqueradeInterface,
        sourceRanges: sourceRanges.length > 0 ? sourceRanges : undefined,
      });
      toast.success('NAT config updated');
      fetchConfig();
    } catch {
      toast.error('Failed to update NAT config');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">NAT Masquerade</h3>

        <div className="space-y-4">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded"
            />
            <div>
              <span className="text-sm font-medium text-gray-700">Enable NAT Masquerade</span>
              <p className="text-xs text-gray-500">Masquerade outbound traffic from the tunnel</p>
            </div>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Masquerade Interface</label>
            <input
              type="text"
              value={masqueradeInterface}
              onChange={(e) => setMasqueradeInterface(e.target.value)}
              placeholder="e.g., eth0"
              className="input-base w-full max-w-xs"
            />
            <p className="mt-1 text-xs text-gray-500">Outbound interface for NAT (typically eth0)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source Ranges</label>
            <textarea
              value={sourceRangesText}
              onChange={(e) => setSourceRangesText(e.target.value)}
              placeholder="One CIDR per line, e.g.&#10;10.200.0.0/24&#10;10.100.0.0/16"
              className="input-base w-full"
              rows={4}
            />
            <p className="mt-1 text-xs text-gray-500">CIDRs to masquerade (one per line). Leave empty to masquerade all tunnel traffic.</p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NATTab;
